import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import '../Dashboard/styles.scss';
import { DataGrid } from '@mui/x-data-grid';
import { updateResearchSegmentStatus } from '../../api';
import './styles.scss';
import ContentModal from './ContentModal';
import SegmentPhotos from './SegmentPhotos';

/**
 * SegmentGrid Component
 */
export default function SegmentGrid({
    segmentData,
    setSegmentData,
    editMode,
    littersBySegmentId,
    selectionModel,
    onSelect,
}) {
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarData, setSnackbarData] = useState({
        status: 'success',
        text: '',
    });
    const [noteValue, setNoteValue] = useState(null);
    const [segmentParamsForPhotos, setSegmentParamsForPhotos] = useState(null); // ID of the clicked Segment row to show SegmentPhotos

    // console.log(
    //     'Rendering SegmentGrid: segmentData status: ',
    //     segmentData.map((s) => s.status),
    // );

    /**
     * Closes any open content modal (Notes or Photos)
     */
    const handleContentClose = () => {
        setNoteValue(null);
        setSegmentParamsForPhotos(null);
    };

    /**
     * Sets the value of the status dropdown for the selected Segment row
     * @param {*} params
     * @param {string} status
     */
    const handleSegmentStatusChange = (params, status) => {
        // Close the Photos modal, if it's open (if Accept or Reject button were clicked to trigger this status change)
        handleContentClose();

        const segmentId = params.row.id;
        console.log('#### handleSegmentStatusChange', segmentId, status);

        // Call API to update Segment status
        updateSegmentStatus(segmentId, status.toUpperCase());

        // Update this Segment status in the state
        const segmentDataWithNewStatus = segmentData.map((segment) => {
            if (segment.id === params.row.id) {
                return { ...params.row, status };
            } else {
                return segment;
            }
        });
        setSegmentData(segmentDataWithNewStatus);
    };

    /**
     * Call API to update the status of the Segment
     * @param {number} segmentId
     * @param {string} nextStatus
     */
    const updateSegmentStatus = async (segmentId, nextStatus) => {
        try {
            // setIsLoading(true);
            const updateStatusResult = await updateResearchSegmentStatus(
                segmentId,
                nextStatus,
            );
            // setIsLoading(false);

            // provide a success or failure snackbar message based on result
            setShowSnackbar(true);
            if (updateStatusResult) {
                setSnackbarData({
                    status: 'success',
                    text: 'Successfully updated segment status!',
                });
            } else {
                setSnackbarData({
                    status: 'error',
                    text: 'Failed to update segment status',
                });
            }
        } catch (err) {
            setShowSnackbar(true);
            setSnackbarData({
                status: 'error',
                text: 'Failed to update segment status',
            });
        }
    };

    const segmentColumns = [
        {
            field: 'taskName',
            headerName: 'Task Name',
            width: 220,
            type: 'string',
            sortable: true,
            renderCell: (params) => {
                return (
                    <abbr title={params.row.taskName}>
                        {params.row.taskName}
                    </abbr>
                );
            },
        },
        {
            field: 'locationName',
            headerName: 'Location Name',
            width: 220,
            type: 'string',
            sortable: true,
            renderCell: (params) => {
                return (
                    <abbr title={params.row.locationName}>
                        {params.row.locationName}
                    </abbr>
                );
            },
        },
        {
            field: 'segmentName',
            headerName: 'Segment Name',
            width: 220,
            type: 'string',
            sortable: true,
            renderCell: (params) => {
                return (
                    <abbr title={params.row.segmentName}>
                        {params.row.segmentName}
                    </abbr>
                );
            },
        },
        {
            field: 'dateCompleted',
            headerName: 'Date Completed',
            width: 130,
            type: 'date',
            sortable: true,
        },
        {
            field: 'distance',
            headerName: 'Distance (m)',
            width: 100,
            type: 'number',
            sortable: true,
        },
        {
            field: 'duration',
            headerName: 'Duration (min)',
            width: 120,
            type: 'string',
            sortable: true,
        },
        {
            field: 'notesMessage',
            headerName: 'Notes',
            width: 100,
            type: 'string',
            sortable: false,
            renderCell: (params) => {
                if (params.row.notes && params.row.notes.length > 0) {
                    return (
                        <button
                            onClick={(event) => {
                                console.log('render notes', params);
                                event.stopPropagation(); // Required to prevent selection

                                setNoteValue(params.row.notes);
                            }}
                        >
                            {params.row.notesMessage}
                        </button>
                    );
                } else return '';
            },
        },
        {
            field: 'photos',
            headerName: 'Photos',
            width: 110,
            sortable: false,
            renderCell: (params) => {
                // if (littersBySegmentId && littersBySegmentId[params.row.id] && littersBySegmentId[params.row.id].length > 0) {
                return (
                    <button
                        onClick={(event) => {
                            console.log('render photos', params);
                            event.stopPropagation(); // Required to prevent selection

                            setSegmentParamsForPhotos(params);
                        }}
                    >
                        {params.row.photos}
                    </button>
                );
                // } else return '';
            },
        },
    ];

    // default status column definition
    let statusColumn = {
        field: 'status',
        headerName: 'Status',
        description: 'Enable edit mode to update a segment status',
        width: 100,
        type: 'string',
        sortable: true,
        editable: true,
        renderCell: (params) => {
            return (
                <select
                    name="status"
                    value={params.row.status}
                    disabled={!editMode}
                    onChange={(e) =>
                        handleSegmentStatusChange(params, e.target.value)
                    }
                >
                    <option value="COMPLETED">Completed</option>
                    <option value="ACCEPTED">Accepted</option>
                    <option value="REJECTED">Rejected</option>
                </select>
            );
        },
    };

    // if in editmode append styling to segment status column
    if (editMode) {
        statusColumn.headerName = 'Edit Status';
        statusColumn.headerClassName = 'segment-grid-status--header';
        statusColumn.cellClassName = 'segment-grid-status--cell';
    }

    // append segment status column to data grid
    segmentColumns.push(statusColumn);

    return (
        <div className="col-12" style={{ height: '500px' }}>
            {segmentData && (
                <DataGrid
                    disableColumnFilter
                    initialState={{
                        sorting: {
                            sortModel: [{ field: 'segmentName', sort: 'asc' }],
                        },
                    }}
                    style={{ backgroundColor: 'white' }}
                    rows={segmentData}
                    columns={segmentColumns}
                    getRowId={(row) => row.id}
                    checkboxSelection={true}
                    disableSelectionOnClick={editMode}
                    selectionModel={selectionModel}
                    onSelectionModelChange={onSelect}
                />
            )}
            {/* Snackbar pop up for update segment feedback */}
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                open={showSnackbar}
                autoHideDuration={6000}
                onClose={() => setShowSnackbar(false)}
            >
                <Alert
                    onClose={() => setShowSnackbar(false)}
                    severity={snackbarData.status || 'success'}
                    sx={{ width: '100%' }}
                >
                    {snackbarData.text || ''}
                </Alert>
            </Snackbar>

            {/* Popup to display photos by litter/bin/hotspot */}
            <ContentModal
                open={!!segmentParamsForPhotos}
                handleClose={handleContentClose}
            >
                <div className="acceptRejectBar m-2">
                    <button
                        className="m-2"
                        onClick={() =>
                            handleSegmentStatusChange(
                                segmentParamsForPhotos,
                                'ACCEPTED',
                            )
                        }
                    >
                        Accept
                    </button>
                    <button
                        className="m-2"
                        onClick={() =>
                            handleSegmentStatusChange(
                                segmentParamsForPhotos,
                                'REJECTED',
                            )
                        }
                    >
                        Reject
                    </button>
                </div>
                {segmentParamsForPhotos ? (
                    <SegmentPhotos
                        litterDataForSegment={
                            littersBySegmentId[segmentParamsForPhotos.row.id]
                        }
                    />
                ) : (
                    <div>No photos</div>
                )}
            </ContentModal>

            {/* Popup to display notes, when a noteValue is set in state */}
            <ContentModal open={!!noteValue} handleClose={handleContentClose}>
                <p className="py-4 px-2">{noteValue}</p>
            </ContentModal>
        </div>
    );
}

SegmentGrid.propTypes = {
    segmentData: PropTypes.array,
    setSegmentData: PropTypes.func,
    editMode: PropTypes.bool,
    littersBySegmentId: PropTypes.object,
    selectionModel: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.array,
    ]),
    onSelect: PropTypes.func,
};
